import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ASSET_API_URL, FILES_API_URL } from "@app/data/const";
import { Query } from "@nowinstore/nowinstore-common";
import {
  RemoteDataService,
  AWSFileInformation,
  ImageUploaderModule,
} from "@nowinstore/nowinstore-core-ui/components";
import * as _ from "lodash";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorModule } from "primeng/paginator";
import { SidebarModule } from "primeng/sidebar";
import { JustifiedGalleryComponent } from "./justified-gallery.component";
import { SelectionIndicatorComponent } from "../platform/selection-indicator.component";
import { FormsModule } from "@angular/forms";
import { File } from "@data/file";

interface AssetQueryParams extends Query.Params {
  source: string;
}

@Component({
  imports: [
    CommonModule,
    FormsModule,
    SidebarModule,
    InputTextModule,
    PaginatorModule,
    ButtonModule,
    JustifiedGalleryComponent,
    SelectionIndicatorComponent,
    ImageUploaderModule,
  ],
  standalone: true,
  selector: "nis-assets-picker",
  templateUrl: "./assets-picker.component.html",
  providers: [],
})
export class AssetsPickerComponent implements OnInit {
  _selectedAsset: string;
  visibleAssets: string[] = [];
  first: number = 0;
  showSidebar: boolean;
  @Input()
  cssMode: boolean;

  @Input()
  inline: boolean;

  @Input()
  cols: number = 4;
  @Input()
  rows: number = 4;
  @Input()
  source: string;
  @Output()
  selectedAssetChange = new EventEmitter<string>();
  debounceTimeout: any;

  _assets: string[] = [];
  @Output() assetsChange = new EventEmitter<string[]>();
  @Output() imageUploaded = new EventEmitter<File>();
  loading: boolean = false;

  @Input()
  set assets(value: string[]) {
    this._assets = value;
    this.assetsChange.emit(this._assets);
  }

  get assets(): string[] {
    return this._assets;
  }

  get limit(): number {
    return this.rows * this.cols;
  }

  @Input()
  set selectedAsset(value: string) {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      if (this._selectedAsset !== value) {
        if (this.cssMode && !value.startsWith("url(")) {
          this._selectedAsset = `url(${encodeURI(value)})`;
        } else {
          this._selectedAsset = value;
        }
        this.selectedAssetChange.emit(this._selectedAsset);
      }
    }, 200);
  }

  get selectedAsset(): string {
    return this._selectedAsset;
  }

  constructor(private remoteDataService: RemoteDataService) {}

  async ngOnInit() {
    await this.loadImages();
  }

  private async loadImages() {
    this.loading = true;
    this.assets = await this.remoteDataService.query<string, AssetQueryParams>(
      ASSET_API_URL,
      {
        source: this.source,
      }
    );
    this.loading = false;
    this.onPageChange({ first: this.first, rows: this.limit });
  }

  onPageChange(event) {
    this.first = event.first;
    this.visibleAssets = this.assets.slice(this.first, this.first + event.rows);
  }

  async onImageUploaded(image: AWSFileInformation) {
    const file = await this.remoteDataService.post<File>(FILES_API_URL, image);
    this.selectedAsset = file.url;
    this.imageUploaded.emit(file);
    await this.loadImages();
  }
}
